import { useEffect, type CSSProperties, type ReactNode } from "react";
import { useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useBranding } from "@/hooks/useBranding";
import { stopReplayOnPublicReport } from "@/sentry";
import type { PublicAuditShareView } from "@shared/site-audit-share";
import { AuditReport } from "@/components/site-audit/report/audit-report";

export default function AuditSharePage() {
  const params = useParams<{ token: string }>();
  const token = params.token ?? "";
  const { brandName } = useBranding();
  const query = useQuery<PublicAuditShareView>({
    queryKey: ["/api/site-audit/share", token],
    enabled: token.length > 0,
    retry: false,
    queryFn: async () => {
      const response = await fetch(`/api/site-audit/share/${encodeURIComponent(token)}`);
      if (!response.ok) throw new Error("Could not load this report");
      return response.json() as Promise<PublicAuditShareView>;
    },
  });

  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains("dark");
    root.classList.remove("dark");
    return () => {
      if (hadDark) root.classList.add("dark");
    };
  }, []);

  useEffect(() => {
    stopReplayOnPublicReport();
  }, []);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);

  const brand = query.data?.brand;
  useEffect(() => {
    const name = brand?.name || brandName || "HeyHiHello";
    document.title = `${name} · Site audit`;
  }, [brand?.name, brandName]);

  if (query.isLoading) {
    return (
      <Shell brandName={brandName || "HeyHiHello"} logoUrl={null} color={null}>
        <p className="text-sm text-foreground/75">Loading the report…</p>
      </Shell>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Shell brandName={brandName || "HeyHiHello"} logoUrl={null} color={null}>
        <h1 className="text-2xl font-semibold tracking-tight">Site audit</h1>
        <p className="mt-3 text-sm leading-relaxed text-foreground/75">
          This report link could not be loaded. Try it again in a moment.
        </p>
      </Shell>
    );
  }

  const view = query.data;
  if (view.state === "ready") return <ReadyReport view={view} />;
  return (
    <Shell brandName={view.brand.name} logoUrl={view.brand.logoUrl} color={view.brand.primaryColorHsl} font={view.brand.headingFont}>
      {view.state === "missing" && (
        <>
          <h1 className="text-2xl font-semibold tracking-tight">This link is not valid</h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground/75">
            The address does not match a report. Check the link you were sent.
          </p>
        </>
      )}
      {view.state === "revoked" && (
        <>
          <h1 className="text-2xl font-semibold tracking-tight">This report link has been turned off</h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground/75">Ask us if you still need the report.</p>
        </>
      )}
      {view.state === "expired" && (
        <>
          <h1 className="text-2xl font-semibold tracking-tight">This report link has expired</h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground/75">
            It stopped working on {formatWhen(view.expiresAt)}. Ask us for a new link.
          </p>
        </>
      )}
    </Shell>
  );
}

function ReadyReport({ view }: { view: Extract<PublicAuditShareView, { state: "ready" }> }) {
  return (
    <Shell
      brandName={view.brand.name}
      logoUrl={view.brand.logoUrl}
      color={view.brand.primaryColorHsl}
      font={view.brand.headingFont}
      showHeader={false}
    >
      <AuditReport
        report={view.report}
        brandName={view.brand.name}
        logoUrl={view.brand.logoUrl}
        dateLabel={formatWhen(view.report.collectedAt ?? "")}
        includeEvidence
        includeSecurity
        titleLevel="h1"
      />
      <p className="text-xs text-foreground/75">
        {view.expiresAt ? `This link works until ${formatWhen(view.expiresAt)}.` : "This link does not expire."}
      </p>
    </Shell>
  );
}

function Shell({
  brandName,
  logoUrl,
  color,
  font,
  showHeader = true,
  children,
}: {
  brandName: string;
  logoUrl: string | null;
  color: string | null;
  font?: string;
  showHeader?: boolean;
  children: ReactNode;
}) {
  const style = {
    colorScheme: "light",
    ...(color ? { "--primary": `hsl(${color})`, "--ring": `hsl(${color})` } : {}),
    ...(font ? { "--font-heading": `"${font}", "Inter", sans-serif` } : {}),
  } as CSSProperties;
  return (
    <main className="min-h-screen bg-background text-foreground" style={style}>
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-12">
        {showHeader ? <header className="mb-8 flex items-center gap-4 border-b border-border pb-6">
          {logoUrl ? (
            <img src={logoUrl} alt="" referrerPolicy="no-referrer" className="h-10 w-auto max-w-[180px] object-contain" />
          ) : (
            <span className="font-heading text-lg font-semibold text-primary">{brandName}</span>
          )}
          {logoUrl ? <span className="text-sm text-foreground/75">{brandName}</span> : null}
        </header> : null}
        <div className="space-y-6">{children}</div>
      </div>
    </main>
  );
}

function formatWhen(iso: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}
